/* Usage & budgets — workspace-level fuel control (Library & admin nav).
   Burn-down vs cap, cost-by-stage / model, per-pod allocation, and live
   admin budget controls. Reuses chart primitives from metrics.jsx (exported
   to window) and reads team/pods from window.MAV. */

const { useState: useUsageState } = React;

function UToggle({ on, onToggle }) {
  return (
    <button type="button" className={'switch' + (on ? ' on' : '')} onClick={onToggle} role="switch" aria-checked={on}>
      <span className="knob"></span>
    </button>
  );
}

function UsageBudgets({ go }) {
  const { Section, Bars, Breakdown, Donut, Trend } = window;
  const { team, pods, memberById } = window.MAV;
  const money = (n) => { n = Number(n) || 0; return '$' + (n >= 1000 ? Math.round(n).toLocaleString() : (Number.isInteger(n) ? String(n) : n.toFixed(2))); };

  // Live OpenRouter account balance + DB cost — the SAME system as the
  // Token-spend dashboard (MetricSpend). Fetched on mount, cached on
  // MAV.credits for cross-screen reuse. The per-dev team.spend rollup stays for
  // the per-pod / top-spender views until the projector writes builds.owner_id.
  const [credits, setCredits] = useUsageState(window.MAV.credits || null);
  React.useEffect(() => {
    let alive = true;
    if (window.GooseAPI && window.GooseAPI.openrouterCredits) {
      window.GooseAPI.openrouterCredits()
        .then((d) => { if (alive && d) { window.MAV.credits = d; setCredits(d); } })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, []);
  const summary = window.MAV.summary;
  const cbs = window.MAV.costByStage;
  const lv = window.liveMetricsView ? window.liveMetricsView() : null;

  const orLive = !!(credits && credits.available && credits.remaining != null);
  const dbCost = summary && summary.total_cost != null ? Number(summary.total_cost) : (cbs ? Number(cbs.totalCost) : 0);
  const teamSpend = team.reduce((s, m) => s + (m.spend || 0), 0);
  // Workspace SPENT = real OpenRouter usage when live, else our DB sum, else the
  // per-dev rollup (mock). Reconciliation: DB attributes `tracked`; the rest is
  // untracked stage cost (QA/vision — ISS-07).
  const spent = orLive ? Number(credits.total_usage) : (dbCost || teamSpend);
  const tracked = dbCost;
  const untracked = orLive ? Math.max(0, spent - tracked) : 0;
  // OpenRouter ACCOUNT fuel — actual credit left, DISTINCT from the policy cap.
  // The cap below is governance ("spend ≤ $X/mo"); this is real money remaining.
  const orBalance = orLive ? Number(credits.total_credits) : null;
  const orRemaining = orLive ? Number(credits.remaining) : null;
  const orPct = (orLive && orBalance > 0) ? Math.round(spent / orBalance * 100) : 0;

  const [cap, setCap] = useUsageState(3000);
  const [warnAt, setWarnAt] = useUsageState(90);
  const [autoPause, setAutoPause] = useUsageState(true);
  const [gateMax, setGateMax] = useUsageState(true);
  const [gateWingman, setGateWingman] = useUsageState(true);

  // VD-01/PY-22: under a codex-default workspace this whole $-centric budgets page is nonsense
  // ($0 sub) → the CodexUsagePanel replaces it. Placed AFTER all hooks (rules-of-hooks). Same
  // RBAC.activeEngine gate as the dashboard (VD-03); non-codex path below is untouched.
  if (window.RBAC && window.RBAC.activeEngine && window.RBAC.activeEngine() === 'codex' && window.CodexUsagePanel) {
    return <div className="page fade-in"><CodexUsagePanel /></div>;
  }

  const pct = cap > 0 ? Math.round(spent / cap * 100) : 0;
  const remaining = cap - spent;
  const projected = Math.round(spent * 1.4 / 50) * 50;          // simple month-end projection
  const overWarn = pct >= warnAt;

  // per-team rollup (per-dev spend is 0 until the projector writes builds.owner_id)
  const podCaps = { lunne: 1200, ehealth: 1000, inboxarmy: 800 };
  const podSpend = pods.map(p => {
    const members = team.filter(m => m.pod === p.id);
    const ps = members.reduce((s, m) => s + (m.spend || 0), 0);
    return { ...p, spend: ps, cap: podCaps[p.id] || 0, members: members.length };
  });

  // top spenders (per-dev — mock until owner_id lands)
  const topDevs = [...team].sort((a, b) => (b.spend || 0) - (a.spend || 0)).slice(0, 5);

  const weekly = [372, 451, 458, 431];   // workspace $ / week — mock (no time-series yet)
  const capPresets = [2000, 3000, 5000, 7500];

  return (
    <div className="page fade-in">
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="row gap-8" style={{ marginBottom: 8 }}>
            <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Admin</span>
            <span className="chip">{orLive ? 'Fuel · live · OpenRouter' : 'Fuel · May 2026'}</span>
          </div>
          <h1 className="page-title">Usage &amp; budgets</h1>
          <p className="page-desc" style={{ maxWidth: 540 }}>Workspace burn-down against the monthly cap, where the fuel goes by stage and model, and the controls that keep the squadron in budget.</p>
        </div>
        <div className="metric-hero" data-usage-spend-source={orLive ? 'openrouter' : 'db'}>
          <div className="mh-v" data-usage-spent>{money(spent)}<small> / {money(cap)}</small></div>
          <div className={'mh-sub ' + (overWarn ? '' : 'up')} style={overWarn ? { color: 'var(--cond)' } : null}>{pct}% of monthly cap</div>
          <div style={{ marginTop: 12 }}><Trend data={weekly} color="var(--brand-orange)" height={56} /></div>
        </div>
      </div>

      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">Spent{orLive ? ' (actual)' : ' this month'}</div><div className="kpi-v" data-kpi="spent">{money(spent)}</div></div>
        <div className="kpi"><div className="kpi-k">Cap remaining</div><div className="kpi-v" style={{ color: remaining > 0 ? 'var(--pass)' : 'var(--fail)' }} data-kpi="cap-remaining">{money(remaining)}</div></div>
        {orLive
          ? <div className="kpi"><div className="kpi-k">OpenRouter credit</div><div className="kpi-v" style={{ color: orPct >= 90 ? 'var(--fail)' : orPct >= 75 ? 'var(--cond)' : 'var(--pass)' }} data-kpi="or-balance">{money(orRemaining)}<small> / {money(orBalance)}</small></div></div>
          : <div className="kpi"><div className="kpi-k">Tokens</div><div className="kpi-v">4.2<small>M</small></div></div>}
        <div className="kpi"><div className="kpi-k">Projected month-end</div><div className="kpi-v" data-kpi="projected">{money(projected)}</div></div>
      </div>

      {/* Real OpenRouter account fuel — distinct from the policy cap. Surfaced
          because the cap view ($ spent / $cap) can read "fine" while the actual
          credit balance is nearly dry. */}
      {orLive && (
        <div className="card card-pad" data-or-account style={{ marginBottom: 20, borderLeft: '4px solid ' + (orPct >= 90 ? 'var(--fail)' : orPct >= 75 ? 'var(--cond)' : 'var(--pass)') }}>
          <div className="row between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>OpenRouter account fuel — {money(orRemaining)} of {money(orBalance)} left <span className="mono muted" style={{ fontSize: 11.5 }}>({orPct}% burned)</span></div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>Your <b>actual credit balance</b> — the {money(cap)} monthly cap below is a spend <i>policy</i>, not your balance.{orPct >= 90 ? <span style={{ color: 'var(--fail)', fontWeight: 600 }}> Under 10% — top up to avoid build failures.</span> : ''}</div>
            </div>
            <div className="meter" style={{ width: 170, height: 10, margin: 0 }}><span style={{ width: Math.min(100, orPct) + '%', background: orPct >= 90 ? 'var(--fail)' : 'var(--grad-warm)' }}></span></div>
          </div>
        </div>
      )}

      {/* burn-down */}
      <Section title="Budget burn-down" right={<span className={'chip ' + (overWarn ? 'pill-fail' : 'pill-cond')} style={{ fontSize: 10.5 }}>{overWarn ? 'Over soft warn' : 'Soft warn at ' + warnAt + '%'}</span>} style={{ marginBottom: 20 }}>
        <div className="row between" style={{ fontSize: 12.5, marginBottom: 8 }}>
          <span className="muted">{money(spent)} spent · {money(remaining)} left</span>
          <span className="mono muted">resets Jun 1</span>
        </div>
        <div className="burn-track">
          <div className="burn-fill" style={{ width: pct + '%' }}></div>
          <div className="burn-warn" style={{ left: warnAt + '%' }} title={'Soft warn at ' + warnAt + '%'}></div>
        </div>
        <div className="row between" style={{ marginTop: 9 }}>
          <span className="muted" style={{ fontSize: 11.5 }}>Projected to reach <b style={{ color: 'var(--ink-2)' }}>{money(projected)}</b> ({Math.round(projected / cap * 100)}%) by Jun 1 at current pace.</span>
          <span className="muted" style={{ fontSize: 11.5 }}>↑ marker = soft warn</span>
        </div>
        {orLive && <p className="muted" style={{ fontSize: 11, marginTop: 9 }}>Spend is OpenRouter's <b>{money(spent)}</b> lifetime account usage. Goose attributes <b>{money(tracked)}</b> to builds still on disk; the <b>{money(untracked)}</b> difference is mostly off-disk lifetime spend — builds whose run dirs were cleaned (not a tracking gap — ISS-10).</p>}
      </Section>

      <div className="metric-grid">
        <Section title="Spend per week" right={<span className="muted" style={{ fontSize: 12 }}>May</span>}>
          <Bars data={weekly} height={140} xlabels={['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4']} />
        </Section>
        <Section title="Cost by stage" right={(cbs && cbs.rows && cbs.rows.length) ? <span className="muted" style={{ fontSize: 11 }}>{orLive ? 'Goose-tracked · ' + money(tracked) : 'live'}</span> : null}>
          <Breakdown rows={(() => {
            if (cbs && cbs.rows && cbs.rows.length) {
              const positive = cbs.rows.filter((r) => Number(r.cost) > 0);
              const total = positive.reduce((a, r) => a + Number(r.cost), 0) || 1;
              const PALETTE = ['var(--brand-orange)', 'var(--brand-amber)', 'var(--brand-yellow)', 'var(--queued)', 'var(--running)', 'var(--cond)'];
              return [...positive].sort((a, b) => Number(b.cost) - Number(a.cost)).map((r, i) => ({
                label: r.name, pct: Math.round((Number(r.cost) / total) * 100),
                value: '$' + Number(r.cost).toFixed(2), color: PALETTE[i % PALETTE.length],
              }));
            }
            return [
              { label: 'Semantic (Sonnet)', pct: 100, value: '$719', color: 'var(--brand-orange)' },
              { label: 'Execution (Gemini)', pct: 67, value: '$479', color: 'var(--brand-amber)' },
              { label: 'QA (Perceptron + Gemini)', pct: 43, value: '$308', color: 'var(--brand-yellow)' },
              { label: 'Planning · Approval (DeepSeek)', pct: 29, value: '$206', color: 'var(--queued)' },
            ];
          })()} />
        </Section>
      </div>

      <div className="metric-grid mt-20">
        <Section title="Spend by team" right={<button className="sec-link" onClick={() => go('admin')}>Team &amp; keys <I.chevron size={14} /></button>}>
          {podSpend.map(p => {
            const pp = Math.round(p.spend / p.cap * 100);
            return (
              <div key={p.id} className="pod-budget">
                <div className="row between" style={{ marginBottom: 6 }}>
                  <div><span style={{ fontSize: 13, fontWeight: 700 }}>{p.name}</span><span className="muted" style={{ fontSize: 11.5, marginLeft: 8 }}>{p.members} devs · {p.lead}</span></div>
                  <span className="mono" style={{ fontSize: 12, color: pp >= 90 ? 'var(--cond)' : 'var(--ink-2)' }}>{money(p.spend)} / {money(p.cap)}</span>
                </div>
                <div className="meter" style={{ margin: 0, height: 8 }}><span style={{ width: pp + '%', background: pp >= 90 ? 'linear-gradient(90deg,var(--cond),var(--brand-orange))' : 'var(--grad-warm)' }}></span></div>
              </div>
            );
          })}
        </Section>
        <Section title="Cost by model" right={lv && lv.costByModelRows && lv.costByModelRows.length ? <span className="muted" style={{ fontSize: 11 }}>{lv.costByModelRows.length} models · live</span> : null}>
          <Breakdown rows={lv && lv.costByModelRows && lv.costByModelRows.length ? lv.costByModelRows : [
            { label: 'claude-sonnet-4.6', pct: 100, value: '$719', color: 'var(--grad-warm)' },
            { label: 'gemini-3.5-flash', pct: 78, value: '$560', color: 'var(--grad-warm)' },
            { label: 'perceptron-mk1', pct: 33, value: '$235', color: 'var(--grad-warm)' },
            { label: 'deepseek-v4-pro', pct: 27, value: '$198', color: 'var(--queued)' },
          ]} />
        </Section>
      </div>

      {/* admin controls */}
      <div className="metric-grid mt-20">
        <Section title="Budget controls" right={<span className="chip" style={{ fontSize: 10.5 }}>Admin only</span>}>
          <div className="bc-block">
            <div className="bc-label">Workspace monthly cap</div>
            <div className="row gap-8 wrap">
              {capPresets.map(c => (
                <button key={c} className={'bc-chip' + (cap === c ? ' on' : '')} onClick={() => setCap(c)}>${c.toLocaleString()}</button>
              ))}
            </div>
          </div>

          <div className="bc-block">
            <div className="row between" style={{ marginBottom: 9 }}>
              <span className="bc-label" style={{ margin: 0 }}>Soft-warn threshold</span>
              <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cond)' }}>{warnAt}%</span>
            </div>
            <input className="fb-slider" type="range" min="50" max="100" step="5" value={warnAt} onChange={e => setWarnAt(parseInt(e.target.value))} style={{ '--pct': ((warnAt - 50) / 50 * 100) + '%' }} />
            <p className="muted" style={{ fontSize: 11, marginTop: 7 }}>Notifies admins &amp; team leads when workspace spend crosses this line (currently ${Math.round(cap * warnAt / 100).toLocaleString()}).</p>
          </div>

          <div className="pref-row">
            <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Auto-pause at cap</div><div className="muted" style={{ fontSize: 12 }}>Hold new builds when the cap is hit; running builds finish</div></div>
            <UToggle on={autoPause} onToggle={() => setAutoPause(v => !v)} />
          </div>
        </Section>

        <Section title="Model-tier gating">
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, marginTop: 0, marginBottom: 14 }}>One Max-quality (Opus) run costs ~17× a DeepSeek-tier run. Gate the pricey tiers so only the right roles can spend that fuel.</p>
          <div className="pref-row">
            <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Max quality <span className="chip" style={{ fontSize: 9.5 }}>Opus</span></div><div className="muted" style={{ fontSize: 12 }}>Restrict to admins only</div></div>
            <UToggle on={gateMax} onToggle={() => setGateMax(v => !v)} />
          </div>
          <div className="pref-row">
            <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Wingman <span className="chip" style={{ fontSize: 9.5 }}>Custom-tuned</span></div><div className="muted" style={{ fontSize: 12 }}>Restrict to team leads &amp; up</div></div>
            <UToggle on={gateWingman} onToggle={() => setGateWingman(v => !v)} />
          </div>
          <div className="pref-row" style={{ borderBottom: 'none' }}>
            <div><div style={{ fontSize: 13.5, fontWeight: 600 }}>Economy &amp; Balanced</div><div className="muted" style={{ fontSize: 12 }}>Available to everyone</div></div>
            <span className="pill pill-pass" style={{ flexShrink: 0 }}><span className="dot"></span>Open</span>
          </div>
        </Section>
      </div>

      {/* top spenders */}
      <Section title="Top spenders this month" right={<button className="sec-link" onClick={() => go('admin')}>Per-dev limits <I.chevron size={14} /></button>} style={{ marginTop: 20 }}>
        <div className="tbl-scroll">
        <table className="tbl">
          <thead><tr><th>Member</th><th>Team</th><th>Builds</th><th>Usage vs limit</th></tr></thead>
          <tbody>
            {topDevs.map(m => {
              const lim = m.limit, up = Math.round(m.used / lim * 100), over = up >= 90;
              const pod = pods.find(p => p.id === m.pod);
              return (
                <tr key={m.id}>
                  <td><div className="row gap-8"><span className="avatar" style={{ width: 26, height: 26, fontSize: 10, background: m.color }}>{m.initials}</span><span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.name}</span></div></td>
                  <td><span className="muted" style={{ fontSize: 12 }}>{pod ? pod.name : '—'}</span></td>
                  <td className="mono">{m.builds}</td>
                  <td>
                    <div className="row gap-10" style={{ alignItems: 'center' }}>
                      <div className="meter" style={{ margin: 0, width: 90, height: 7 }}><span style={{ width: up + '%', background: over ? 'linear-gradient(90deg,var(--cond),var(--brand-orange))' : 'var(--grad-warm)' }}></span></div>
                      <span className="mono" style={{ fontSize: 11.5, color: over ? 'var(--cond)' : 'var(--muted)' }}>{money(m.used)} / {money(lim)}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Section>
    </div>
  );
}

window.UsageBudgets = UsageBudgets;
